import { useReducer, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import axios from 'axios';
import {
  CustomerRequestTypesArray,
  CustomerRequestTypes,
} from '@lambda/commons/smartforms/types';
import { CtaButton } from '@/components/Cta/Cta';
import Dropdown from '@/components/smartForm/Dropdown';
import infoIconBlack from '@/public/icons/product/infoIconBlack.svg';
import MediaUploadZoneOld from '@/components/smartForm/MediaUploadZoneOld';
import { UploadS3Url } from '@/lib/smartForm/types';
import { logger } from '@/lib/logger';
import { isValidEmail } from '@/lib/isValidEmail';
import i18n from '@/settings/i18n';

const t = i18n.init('help');

const getContactUsDropdownConfig = () => ({
  labelTitle: '',
  options: [
    {
      optionLabel: t`Delivery / shipping enquiry`,
      value: 'OPT-1',
    },
    {
      optionLabel: t`I have an issue with my order`,
      value: 'OPT-2',
    },
    {
      optionLabel: t`I want to cancel or modify my order`,
      value: 'OPT-3',
    },
    {
      optionLabel: t`I would like to get an invoice`,
      value: 'OPT-4',
    },
    {
      optionLabel: t`I have not ordered yet but have some questions!`,
      value: 'OPT-5',
    },
    {
      optionLabel: t`Partnerships & PR enquiries`,
      value: 'OPT-6',
    },
  ],
  defaultOptionLabel: t`How can we help?`,
  defaultValue: '',
});

const getRequestTypeDropdownConfig = () => ({
  labelTitle: '',
  options: CustomerRequestTypesArray.map((type) => ({
    optionLabel: t(type),
    value: type,
  })),
  defaultOptionLabel: '',
  defaultValue: '',
});

type ContactUsFormState = {
  email: string;
  emailErrorMessage: string;
  requestType: CustomerRequestTypes | null;
  requestTypeErrorMessage: string;
  subject: string;
  subjectErrorMessage: string;
  description: string;
  descriptionErrorMessage: string;
  fullname: string;
  fullnameErrorMessage: string;
  orderId?: string;
  files: Array<File>;
};

type ReducerAction =
  | {
      type:
        | 'setEmail'
        | 'setRequestType'
        | 'setSubject'
        | 'setDescription'
        | 'setFullname'
        | 'setOrderId';
      payload: string;
    }
  | {
      type: 'setFiles';
      payload: Array<File>;
    }
  | {
      type: 'setErrorMessage';
      payload: Array<{
        field: 'email' | 'requestType' | 'subject' | 'description' | 'fullname';
        message: string;
      }>;
    }
  | { type: 'clearErrorMessage'; payload: null };

const contactUsFormReducer = (
  state: ContactUsFormState,
  action: ReducerAction,
): ContactUsFormState => {
  const setterActionMapping = {
    setEmail: { ...state, email: action.payload as string },
    setRequestType: { ...state, requestType: action.payload as string },
    setSubject: { ...state, subject: action.payload as string },
    setDescription: { ...state, description: action.payload as string },
    setFullname: { ...state, fullname: action.payload as string },
    setOrderId: { ...state, orderId: action.payload as string },
    setFiles: { ...state, files: action.payload as Array<File> },
  };

  // have to use this rather than include or else 2 lines below will complain about
  // 'setErrorMessage' not found
  if (
    action.type !== 'clearErrorMessage' &&
    action.type !== 'setErrorMessage'
  ) {
    return setterActionMapping[action.type];
  }

  if (action.type === 'setErrorMessage') {
    const newState: ContactUsFormState = { ...state };

    action.payload.forEach((payload) => {
      newState[`${payload.field}ErrorMessage`] = payload.message;
    });

    return newState;
  }

  return {
    ...state,
    emailErrorMessage: '',
    requestTypeErrorMessage: '',
    subjectErrorMessage: '',
    descriptionErrorMessage: '',
    fullnameErrorMessage: '',
  };
};

const textInputClass =
  'my-1 appearance-none rounded-sm border border-solid border-gray-500  p-3 text-gray-700 outline-1 outline-gray-700';

export default function ContactUsForm(props: { isLoggedIn: boolean | null }) {
  const [formInputState, dispatch] = useReducer(contactUsFormReducer, {
    email: '',
    emailErrorMessage: '',
    requestType: null,
    requestTypeErrorMessage: '',
    subject: '',
    subjectErrorMessage: '',
    description: '',
    descriptionErrorMessage: '',
    fullname: '',
    fullnameErrorMessage: '',
    files: [],
  });
  const contactUsDropdownConfig = getContactUsDropdownConfig();
  const requestTypeDropdownConfig = getRequestTypeDropdownConfig();
  const [contactUsOption, setContactUsOption] = useState('');
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const onChangedContactUsOptions = (value: string) => {
    setContactUsOption(value);
  };

  const onChangeRequestTypeOptions = (value: string) => {
    dispatch({ type: 'setRequestType', payload: value });
  };

  const uploadFiles = async () => {
    if (formInputState.files.length === 0) {
      return [];
    }

    const { files } = formInputState;

    const fileNames = files.map((file) => ({
      filename: file.name,
      type: file.type,
    }));
    const response = await axios.post<Array<UploadS3Url>>(
      '/api/accounts/zendesk/upload-urls',
      {
        fileNames,
        orderNumber: 'general',
      },
    );

    const mappedUrl: UploadS3Url[] = response?.data || [];
    const promises = mappedUrl.map(async (res, index) => {
      const headers = {
        'Content-Type': files[index].type,
      };

      return axios
        .put(res.signedUrl, files[index], { headers })
        .catch((error) =>
          logger.warn({ error }, `Unable to upload image file to S3`),
        );
    });

    await Promise.all(promises);

    const s3Contents = mappedUrl.map((url, index) => ({
      contentType: files[index].type,
      fileName: files[index].name,
      bucketUrl: url.bucketUrl,
    }));

    const uploadAttachmentResponse = await axios.post<Array<string>>(
      `/api/accounts/zendesk/upload`,
      {
        s3Contents,
        orderNumber: 'general',
      },
    );

    return uploadAttachmentResponse.data || [];
  };

  const onSubmitForm = async () => {
    dispatch({ type: 'clearErrorMessage', payload: null });
    const requiredFields: Record<string, string> = {
      email: t`Email`,
      requestType: t`Nature of Request`,
      subject: t`Subject`,
      description: t`Description`,
      fullname: t`Full Name`,
    };

    const errorMessages = Object.keys(requiredFields)
      .map((field) => {
        const fieldValue = (
          formInputState as unknown as Record<string, string>
        )[field];

        if (fieldValue == null || fieldValue.length === 0) {
          return {
            field,
            message: t`${{ field: requiredFields[field] }} is required`,
          };
        }

        // Custom email validation
        if (field === 'email' && !isValidEmail(fieldValue)) {
          return { field, message: t`Please enter a valid email address` };
        }

        return undefined;
      })
      .filter(Boolean) as Array<{ field: string; message: string }>;

    if (errorMessages.length !== 0) {
      dispatch({ type: 'setErrorMessage', payload: errorMessages as any });

      return;
    }

    setLoading(true);
    const requestBody: any = {
      email: formInputState.email,
      subject: formInputState.subject,
      description: formInputState.description,
      type: formInputState.requestType,
      customerName: formInputState.fullname,
      orderId: formInputState.orderId,
    };

    if (formInputState.files.length !== 0) {
      try {
        const uploads = await uploadFiles();

        requestBody.uploads = uploads;
      } catch (error) {
        setLoading(false);
        logger.error('Error uploading file');

        return;
      }
    }

    try {
      await axios.post('/api/zendesk', requestBody);
      setFormSubmitted(true);
    } catch (error) {
      logger.error({ error }, 'Error submitting ticket to zendesk');
    }

    setLoading(false);
  };

  return (
    <>
      {formSubmitted ? (
        <div className="bg-green py-8 xxs:py-12 md:py-16">
          <div className="reebelo-container">
            <div className="bg-gray-100 drop-shadow-xl">
              <div className="flex flex-col p-6 xs:p-8 md:p-12">
                <div className="mb-2 text-xl font-bold !leading-tight xs:mb-1">
                  {t`We’ve got your request. Thank you for contacting us!`}
                </div>
                <div className="!leading-tight">
                  {t`Our customer service team will come back to you within 2 business days.`}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white py-8 xxs:py-12 md:py-16">
          <div className="reebelo-container">
            <div className="flex flex-col">
              <div className="text-xl font-bold">{t`Contact us`}</div>
              <Dropdown
                config={contactUsDropdownConfig}
                callback={onChangedContactUsOptions}
              />
              {['OPT-1', 'OPT-2', 'OPT-3', 'OPT-4'].includes(
                contactUsOption,
              ) && (
                <div className="mt-6 flex w-full items-center border-gray-400 bg-[#F4FEFF] px-4 py-5 text-sm drop-shadow-lg xxs:px-5 xxs:py-6 xs:px-7 xs:py-8 xs:text-base xs:drop-shadow-xl">
                  <div className="relative w-[14px] xxs:w-[16px]">
                    <Image
                      src={infoIconBlack}
                      alt={infoIconBlack}
                      className="w-full object-cover"
                    />
                  </div>
                  <div className="ml-3 w-[calc(100%-14px)] !leading-tight xxs:w-[calc(100%-16px)] xs:ml-4">
                    <p className="!leading-tight">
                      {t`For faster & more personalised help, please contact us directly via`}{' '}
                      <Link
                        href={`/account/${
                          props.isLoggedIn ? 'orders' : 'register'
                        }`}
                        className="font-bold underline"
                      >
                        {t`your customer account`}
                      </Link>
                      {t`, using the Help, Return, or Warranty buttons.`}
                    </p>
                    {!props.isLoggedIn && (
                      <p className="!leading-tight">
                        {t`No account?`}{' '}
                        <Link
                          href="/account/register"
                          className="font-bold underline"
                        >
                          {t`Create one`}
                        </Link>{' '}
                        {t`in a minute to fetch your past orders.`}
                      </p>
                    )}
                    <br />
                    <p className="!leading-tight">
                      {t`If you haven't placed any order yet, you can use the form below and our team will get back to you within two business days.`}
                    </p>
                  </div>
                </div>
              )}
              {contactUsOption.length !== 0 && (
                <section>
                  <div className="mt-10 flex flex-col">
                    <label htmlFor="email" className="text-sm">
                      {t`Your Email`}
                    </label>
                    <input
                      id="email"
                      className={textInputClass}
                      type="email"
                      onChange={(event) => {
                        dispatch({
                          type: 'setEmail',
                          payload: event.target.value,
                        });
                      }}
                    />
                    {formInputState.emailErrorMessage && (
                      <span className="text-sm text-red">
                        {formInputState.emailErrorMessage}
                      </span>
                    )}
                  </div>
                  <div className="mt-5 flex flex-col">
                    <label className="text-sm">{t`Nature of Request`}</label>
                    <Dropdown
                      config={requestTypeDropdownConfig}
                      callback={onChangeRequestTypeOptions}
                    />
                    {formInputState.requestTypeErrorMessage && (
                      <span className="text-sm text-red">
                        {formInputState.requestTypeErrorMessage}
                      </span>
                    )}
                  </div>
                  <div className="mt-5 flex flex-col">
                    <label htmlFor="subject" className="text-sm">
                      {t`Subject`}
                    </label>
                    <input
                      id="subject"
                      className={textInputClass}
                      type="text"
                      onChange={(event) =>
                        dispatch({
                          type: 'setSubject',
                          payload: event.target.value,
                        })
                      }
                    />
                    {formInputState.subjectErrorMessage && (
                      <span className="text-sm text-red">
                        {formInputState.subjectErrorMessage}
                      </span>
                    )}
                  </div>
                  <div className="mt-5 flex flex-col">
                    <label htmlFor="description" className="text-sm">
                      {t`Description`}
                    </label>
                    <textarea
                      id="description"
                      rows={5}
                      className={textInputClass}
                      onChange={(event) =>
                        dispatch({
                          type: 'setDescription',
                          payload: event.target.value,
                        })
                      }
                    ></textarea>
                    {formInputState.descriptionErrorMessage && (
                      <span className="text-sm text-red">
                        {formInputState.descriptionErrorMessage}
                      </span>
                    )}
                  </div>
                  <div className="mt-5 flex flex-col">
                    <label htmlFor="fullName" className="text-sm">
                      {t`Full Name`}
                    </label>
                    <input
                      id="fullName"
                      className={textInputClass}
                      type="text"
                      onChange={(event) =>
                        dispatch({
                          type: 'setFullname',
                          payload: event.target.value,
                        })
                      }
                    />
                    {formInputState.fullnameErrorMessage && (
                      <span className="text-sm text-red">
                        {formInputState.fullnameErrorMessage}
                      </span>
                    )}
                  </div>
                  <div className="mt-5 flex flex-col">
                    <label htmlFor="orderId" className="text-sm">
                      {t`Order ID (optional)`}
                    </label>
                    <input
                      id="orderId"
                      className={textInputClass}
                      type="text"
                      onChange={(event) =>
                        dispatch({
                          type: 'setOrderId',
                          payload: event.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="mt-5 flex flex-col">
                    <label htmlFor="orderId" className="-mb-1 text-sm sm:mb-2">
                      {t`Attachments (optional)`}
                    </label>
                    <MediaUploadZoneOld
                      type="image"
                      descriptionType="optional"
                      showTitle={false}
                      showFooter={false}
                      files={formInputState.files}
                      callback={(files: Array<File>) => {
                        dispatch({ type: 'setFiles', payload: files });
                      }}
                    />
                  </div>
                  <CtaButton
                    className="group relative -mt-1 w-full border border-gray-700 bg-gray-700 pb-10 pt-6 font-bold duration-200 ease-in-out sm:rounded-b-md sm:rounded-t-none sm:py-4"
                    loading={loading}
                    onClick={onSubmitForm}
                  >
                    {t`SUBMIT`}
                  </CtaButton>
                </section>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
